import { Routes } from '@angular/router';
import { SignupComponent } from './features/auth/pages/signup/signup.component';
import { CheckEmailComponent } from './features/auth/pages/check-email/check-email.component';
import { ResetPasswordComponent } from './features/auth/pages/reset-password/reset-password.component';
import { TempPasswordComponent } from './features/auth/pages/temp-password/temp-password.component';
import { SigninComponent } from './features/auth/pages/signin/signin.component';
import { OnboardingComponent } from './features/auth/pages/onboarding/onboarding.component';
import { OverviewComponent } from './features/dashboard/pages/overview/overview.component';
import { ElectricityComponent } from './features/dashboard/pages/electricity/electricity.component';
import { GasComponent } from './features/dashboard/pages/gas/gas.component';
import { InvoiceElectricityPageComponent } from './features/invoice/pages/invoice-electricity-page/invoice-electricity-page.component';
import { InvoiceGasPageComponent } from './features/invoice/pages/invoice-gas-page/invoice-gas-page.component';
import { OptimizationElectricityPageComponent } from './features/optimization/pages/optimization-electricity-page/optimization-electricity-page.component';
import { OptimizationGasPageComponent } from './features/optimization/pages/optimization-gas-page/optimization-gas-page.component';
import { LocationsOverviewPageComponent } from './features/locations/pages/locations-overview-page/locations-overview-page.component';
import { ArchiveOverviewPageComponent } from './features/archive/pages/archive-overview-page/archive-overview-page.component';
import { FileUploadOverviewComponent } from './features/file-upload/pages/file-upload-overview/file-upload-overview.component';
import { SettingsCompanyInformationPageComponent } from './features/settings/pages/settings-company-information-page/settings-company-information-page.component';
import { SettingsMyProfilePageComponent } from './features/settings/pages/settings-my-profile-page/settings-my-profile-page.component';
import { SettingsTeamPageComponent } from './features/settings/pages/settings-team-page/settings-team-page.component';
import { SettingsPlansPageComponent } from './features/settings/pages/settings-plans-page/settings-plans-page.component';
import { SettingsBillingPageComponent } from './features/settings/pages/settings-billing-page/settings-billing-page.component';
import { LogoutComponent } from './features/auth/pages/logout/logout.component';
import { authGuard } from './core/guards/auth.guard';
import { SolarPVRoofPageComponent } from './features/services/pages/solar-pvroof-page/solar-pvroof-page.component';
import { ServicesGreenMobilityPageComponent } from './features/services/pages/solar-green-mobility-page/services-green-mobility-page.component';
import { AdminPanelPageComponent } from './features/admin/pages/admin-panel-page/admin-panel-page.component';
import { adminGuard } from './core/guards/admin.guard';
import { loggedInGuard } from './core/guards/logged-in.guard';
import { ServicesESGReportingsPageComponent } from './features/services/pages/services-esgreportings-page/services-esgreportings-page.component';
import { AdminSalesAgentsPageComponent } from './features/admin/pages/admin-sales-agents-page/admin-sales-agents-page.component';
import { AdminSalesAgentsDataPageComponent } from './features/admin/pages/admin-sales-agents-data-page/admin-sales-agents-data-page.component';
import { AdminFileDataPageComponent } from './features/admin/pages/admin-file-data-page/admin-file-data-page.component';
import { AdminJsonGeneratorPageComponent } from './features/admin/pages/admin-json-generator-page/admin-json-generator-page.component';
import { AdminFileExtractionHistoryComponent } from './features/admin/pages/admin-file-extraction-history/admin-file-extraction-history.component';
import { pendingUploadGuard } from './core/guards/pending-upload.guard';
import { AdminElectricityInvoiceCheckReviewPageComponent } from './features/admin/pages/admin-electricity-invoice-check-review-page/admin-electricity-invoice-check-review-page.component';
import { AdminGasInvoiceCheckReviewPageComponent } from './features/admin/pages/admin-gas-invoice-check-review-page/admin-gas-invoice-check-review-page.component';
import { AdminCompanyDashboardComponent } from './features/admin/pages/upsell-engine/admin-company-dashboard/admin-company-dashboard.component';
import { AdminProductsServicesComponent } from './features/admin/pages/upsell-engine/admin-products-services/admin-products-services.component';
import { AdminOpportunitiesComponent } from './features/admin/pages/upsell-engine/admin-opportunities/admin-opportunities.component';
import { AdminSignalsPageComponent } from './features/admin/pages/upsell-engine/admin-signals-page/admin-signals-page.component';
import { AdminIndicatorsPageComponent } from './features/admin/pages/upsell-engine/admin-indicators-page/admin-indicators-page.component';
import { AdminVariablesPageComponent } from './features/admin/pages/upsell-engine/admin-variables-page/admin-variables-page.component';
import { AdminFileManagementPageComponent } from './features/admin/pages/admin-file-management-page/admin-file-management-page.component';
import { dashboardGuard } from './core/guards/dashboard.guard';
import { AdminPromotionCodesPageComponent } from './features/admin/pages/admin-promotion-codes-page/admin-promotion-codes-page.component';
import { AdminExtractionQAPageComponent } from './features/admin/pages/admin-extraction-qa-page/admin-extraction-qa-page.component';
import { AdminBimestraleExtractionQAPageComponent } from './features/admin/pages/admin-bimestrale-extraction-qa-page/admin-bimestrale-extraction-qa-page.component';
import { MfaComponent } from './features/auth/pages/mfa/mfa.component';
import { AccountDeletedComponent } from './features/settings/pages/account-deleted/account-deleted.component';

export const routes: Routes = [
    // Default redirect to signup
    { path: '', redirectTo: '/auth/signup', pathMatch: 'full' },

    // Redirects for commonly used routes
    { path: 'signup', redirectTo: '/auth/signup', pathMatch: 'full' },
    { path: 'signin', redirectTo: '/auth/signin', pathMatch: 'full' },
    { path: 'reset-password', redirectTo: '/auth/reset-password', pathMatch: 'full' },
    { path: 'logout', redirectTo: '/auth/logout', pathMatch: 'full' },

    {
        path: 'auth',
        canDeactivate: [pendingUploadGuard],
        children: [
            { path: '', redirectTo: 'signup', pathMatch: 'full' },
            { path: 'signup', component: SignupComponent, canActivate: [loggedInGuard] },
            { path: 'signin', component: SigninComponent, canActivate: [loggedInGuard] },
            { path: 'check-email', component: CheckEmailComponent, canActivate: [loggedInGuard] },
            { path: 'reset-password', component: ResetPasswordComponent, canActivate: [loggedInGuard] },
            { path: 'temp-password', component: TempPasswordComponent, canActivate: [loggedInGuard] }, // only works if the 'email' query parameter is present in the URL (e.g., /temp-password?email=test@mail.com).
            { path: 'onboarding', component: OnboardingComponent },
            { path: 'logout', component: LogoutComponent },
            { path: 'mfa', component: MfaComponent },
        ],
    },

    {
        path: 'dashboard',
        canActivate: [authGuard, dashboardGuard],
        canDeactivate: [pendingUploadGuard],
        children: [
            { path: '', redirectTo: 'overview', pathMatch: 'full' },
            { path: 'overview', component: OverviewComponent },
            { path: 'electricity', component: ElectricityComponent },
            { path: 'gas', component: GasComponent },
        ],
    },

    {
        path: 'invoice',
        canActivate: [authGuard],
        canDeactivate: [pendingUploadGuard],
        children: [
            { path: '', redirectTo: 'electricity', pathMatch: 'full' },
            { path: 'electricity', component: InvoiceElectricityPageComponent },
            { path: 'gas', component: InvoiceGasPageComponent },
        ],
    },

    // TODO: DSantek - Re-enable optimization routes once Non-Group clients are onboarded
    /*
    Optimization section is hidden for product launch.
    {
        path: 'optimization',
        canActivate: [authGuard],
        canDeactivate: [pendingUploadGuard],
        children: [
            { path: '', redirectTo: 'electricity', pathMatch: 'full' },
            { path: 'electricity', component: OptimizationElectricityPageComponent },
            { path: 'gas', component: OptimizationGasPageComponent },
        ],
    },
    */

    {
        path: 'services',
        canActivate: [authGuard],
        canDeactivate: [pendingUploadGuard],
        children: [
            { path: '', redirectTo: 'pv-roof', pathMatch: 'full' },
            { path: 'pv-roof', component: SolarPVRoofPageComponent },
            { path: 'esg-reportings', component: ServicesESGReportingsPageComponent },
            { path: 'green-mobility', component: ServicesGreenMobilityPageComponent },
        ],
    },

    {
        path: 'locations',
        canActivate: [authGuard],
        canDeactivate: [pendingUploadGuard],
        children: [
            { path: '', redirectTo: 'overview', pathMatch: 'full' },
            { path: 'overview', component: LocationsOverviewPageComponent },
        ],
    },

    {
        path: 'archive',
        canActivate: [authGuard],
        canDeactivate: [pendingUploadGuard],
        children: [
            { path: '', redirectTo: 'overview', pathMatch: 'full' },
            { path: 'overview', component: ArchiveOverviewPageComponent },
        ],
    },

    {
        path: 'file-upload',
        canActivate: [authGuard],
        canDeactivate: [pendingUploadGuard],
        children: [
            { path: '', redirectTo: 'add', pathMatch: 'full' },
            { path: 'add', component: FileUploadOverviewComponent },
        ],
    },

    {
        path: 'settings',
        canActivate: [authGuard],
        canDeactivate: [pendingUploadGuard],
        children: [
            { path: '', redirectTo: 'company', pathMatch: 'full' },
            { path: 'company', component: SettingsCompanyInformationPageComponent },
            { path: 'profile', component: SettingsMyProfilePageComponent },
            { path: 'team', component: SettingsTeamPageComponent },
            { path: 'billing', component: SettingsBillingPageComponent },
            { path: 'plans', component: SettingsPlansPageComponent },
        ],
    },

    {
        path: 'admin',
        canActivate: [authGuard, adminGuard],
        canDeactivate: [pendingUploadGuard],
        children: [
            { path: '', redirectTo: 'sales-agents', pathMatch: 'full' },
            { path: 'market-data', component: AdminPanelPageComponent },
            { path: 'sales-agents', component: AdminSalesAgentsPageComponent },
            { path: 'sales-agents-data', component: AdminSalesAgentsDataPageComponent },
            { path: 'file-data', component: AdminFileDataPageComponent },
            { path: 'json-generator', component: AdminJsonGeneratorPageComponent },
            { path: 'file-extraction', component: AdminFileExtractionHistoryComponent },
            { path: 'electricity-invoice-check-review', component: AdminElectricityInvoiceCheckReviewPageComponent },
            { path: 'gas-invoice-check-review', component: AdminGasInvoiceCheckReviewPageComponent },
            { path: 'company-dashboard', component: AdminCompanyDashboardComponent },
            { path: 'products-services', component: AdminProductsServicesComponent },
            { path: 'opportunities', component: AdminOpportunitiesComponent },
            { path: 'signals', component: AdminSignalsPageComponent },
            { path: 'indicators', component: AdminIndicatorsPageComponent },
            { path: 'variables', component: AdminVariablesPageComponent },
            { path: 'file-management', component: AdminFileManagementPageComponent },
            { path: 'promotion-codes', component: AdminPromotionCodesPageComponent },
            { path: 'extraction-qa', component: AdminExtractionQAPageComponent },
            { path: 'bimestrale-extraction-qa', component: AdminBimestraleExtractionQAPageComponent },
        ],
    },

    { path: 'account-deleted', component: AccountDeletedComponent },

    // Redirect for undefined paths
    { path: '**', redirectTo: '/auth/signup', pathMatch: 'full' },
];
